import { AddIcon } from "@chakra-ui/icons";
import { Alert, AlertDescription, AlertIcon, Box, Button, Flex, Heading, HStack, Spinner, Text } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIntercom } from "react-use-intercom";
import {
  type LibraryElementContentDto,
  LibraryElementTypeDto,
  MinimumRequirementDto,
  MinimumRequirementPatchDto,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { isEditableInWizard } from "../../../../sourcing-event/changes/changeScope";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useConfirmationModal } from "../../../../../common/useConfirmationModal";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { ApplyTemplateButton } from "../components/apply-template/ApplyTemplateButton";
import { AddedFromLibraryBanner } from "../../../../../common/library/AddedFromLibraryBanner";
import { AddRequirementPanel } from "../../../../../common/library/AddRequirementPanel";
import { useAddFromLibrary } from "../../../../../common/library/useAddFromLibrary";
import { SectionEmptyState } from "../../../../../common/library/SectionEmptyState";
import {
  MinimumRequirementDrawer,
  type MinimumRequirementFormValues,
} from "../components/MinimumRequirementDrawer";
import { MinimumRequirementList } from "../components/MinimumRequirementList";

// t('Minimum requirements') t('Add requirement') t('Delete requirement')
// t('Are you sure you want to delete this requirement?') t('Delete')
// t('Requirements are locked after publication')
// t('Minimum requirements are pass/fail conditions every supplier must meet to be considered.')
// t('Add minimum requirements') t('minimum requirements')
// t('New requirement') t('From the library')
// t('No minimum requirements in this procurement')

type DrawerMode =
  | { kind: "add" }
  | { kind: "edit"; index: number; initialValues: MinimumRequirementFormValues };

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
  isActive: boolean;
}

export const MinimumRequirementsStep = ({ eventId, previousStep, nextStep, nextStepLabel, isActive }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const [drawerState, setDrawerState] = useState<DrawerMode | null>(null);
  const [drawerKey, setDrawerKey] = useState(0);
  const confirmationModal = useConfirmationModal();
  const { update: updateIntercom } = useIntercom();

  const isLocked = !isEditableInWizard(state);
  const items = useMemo(() => state.eventData.minimumRequirements ?? [], [state.eventData.minimumRequirements]);
  const isSaving = (() => {
    const s = fieldStatus("eventData.minimumRequirements");
    return s === "saving" || s === "pending";
  })();

  useEffect(() => {
    if (!isActive) setDrawerState(null);
  }, [isActive]);

  const isDrawerOpen = drawerState !== null;
  useEffect(() => {
    updateIntercom({ hideDefaultLauncher: isDrawerOpen });
  }, [isDrawerOpen, updateIntercom]);

  // The whole list is sent on every change (merge-patch replaces it wholesale). Existing items
  // carry their id so the BFF keeps it stable; new items omit it and the BFF assigns one.
  const commit = useCallback(
    (next: MinimumRequirementDto[]) => {
      const patch: MinimumRequirementPatchDto[] = next.map((r) => ({
        id: r.id ?? undefined,
        title: r.title,
        description: r.description,
        responseType: r.responseType,
      }));
      updateField({ eventData: { minimumRequirements: patch } });
    },
    [updateField]
  );

  const library = useAddFromLibrary({ items, commit });
  const { save } = library;

  // A batch from the library is one edit, so Undo is one edit back: keep the list as it was rather than
  // trying to subtract the added rows afterwards.
  const addFromLibrary = (contents: LibraryElementContentDto[]) =>
    library.add(contents.flatMap((c) => (c.minimum ? [{ ...c.minimum, id: undefined }] : [])));

  const handleSubmit = useCallback(
    async (values: Omit<MinimumRequirementDto, "id">) => {
      const newList =
        drawerState?.kind === "edit"
          ? items.map((item, i) => (i === drawerState.index ? { ...item, ...values } : item))
          : [...items, values];
      save(newList);
      setDrawerState(null);
    },
    [items, save, drawerState]
  );

  const handleEdit = useCallback(
    (index: number) => {
      const item = items[index];
      setDrawerKey((k) => k + 1);
      setDrawerState({
        kind: "edit",
        index,
        initialValues: {
          responseType: item.responseType,
          title: item.title,
          description: item.description,
        },
      });
    },
    [items]
  );

  const handleDelete = useCallback(
    (index: number) => {
      confirmationModal.show({
        title: t("Delete requirement"),
        description: t("Are you sure you want to delete this requirement?"),
        confirmButtonText: t("Delete") ?? "",
        confirmButtonColor: "red",
        onConfirmed: () => save(items.filter((_, i) => i !== index)),
      });
    },
    [items, save, confirmationModal, t]
  );


  return (
    <Box pb="8" pt="10">
      <Flex justify="space-between" align="center" mb="2">
        <Heading as="h2" size="md">
          {t("Minimum requirements")} {isSaving && <Spinner size="xs" />}
        </Heading>
        <HStack spacing="2" flexShrink={0}>
          {/* Both ways to fill the section are one click away; an empty section carries them instead. */}
          {!isLocked && items.length > 0 && (
            <>
              <Button size="sm" variant="outline" colorScheme="teal" leftIcon={<AddIcon />} onClick={() => library.openPanel("new")}>
                {t("New requirement")}
              </Button>
              <Button size="sm" colorScheme="teal" onClick={() => library.openPanel("library")}>
                {t("From the library")}
              </Button>
            </>
          )}
          <ApplyTemplateButton eventId={eventId} preselect={["minimum"]} />
        </HStack>
      </Flex>
      <Text fontSize="sm" color="smMuted" mb="6">
        {t("Minimum requirements are pass/fail conditions every supplier must meet to be considered.")}
      </Text>

      {isLocked && (
        <Alert status="info" borderRadius="lg" mb="6">
          <AlertIcon />
          <AlertDescription>{t("Requirements are locked after publication")}</AlertDescription>
        </Alert>
      )}

      {library.lastAdded && <AddedFromLibraryBanner count={library.lastAdded.count} onUndo={library.undo} />}

      <Box mb="8">
        {items.length === 0 && !isLocked ? (
          <SectionEmptyState
            title={t("No minimum requirements in this procurement")}
            type={LibraryElementTypeDto.Minimum}
            onAddFromLibrary={() => library.openPanel("library")}
            onWriteNew={() => library.openPanel("new")}
          />
        ) : (
          <MinimumRequirementList
            items={items}
            onAdd={() => library.openPanel("library")}
            onEdit={handleEdit}
            onDelete={handleDelete}
            markedCount={library.markedCount}
          />
        )}
      </Box>

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />

      <AddRequirementPanel
        isOpen={library.isPanelOpen}
        onClose={library.closePanel}
        type={LibraryElementTypeDto.Minimum}
        title={t("Add minimum requirements")}
        typeNoun={t("minimum requirements")}
        initialTab={library.tab}
        isAlreadyInSection={(el) => items.some((i) => i.title === el.content.minimum?.title)}
        onAdd={addFromLibrary}
      />

      <MinimumRequirementDrawer
        key={drawerKey}
        isOpen={drawerState !== null}
        isSaving={isSaving}
        mode={drawerState?.kind ?? "add"}
        defaultValues={drawerState?.kind === "edit" ? drawerState.initialValues : undefined}
        onSubmit={handleSubmit}
        onClose={() => setDrawerState(null)}
      />

      {confirmationModal.modal}
    </Box>
  );
};
